import React, { FC, useEffect, useMemo, useState } from 'react'
import i18n from 'i18next'
import { ActivityLog } from '@types'
import { getActivityLogFn } from 'api'
import { BlackParagraph } from 'components/atoms/Paragraph'
import Table from 'components/organisms/Table'
import Header from 'components/organisms/Table/atoms/Header'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import {
  Column,
  SortingRule,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table'
import { emptyArray, sortByAlphabet, sortByDate } from 'utils/array'
import { DATE_TIME_FORMAT, formatDate } from 'utils/date'
import { highlight } from 'utils/highlight'
import Label from 'components/atoms/Label'
import { Page, PageHeader } from 'components/organisms/layout'
import { useUserContext } from 'contexts/UserContext'
import { Roles } from 'utils/consts'
import { LogsFilter } from './components/Filter'

interface FormattedActivityLog extends Omit<ActivityLog, 'admin_user'> {
  adminRole: ActivityLog['admin_user']['type']
}

const columns: Column<FormattedActivityLog>[] = [
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.date')} />
    },
    accessor: 'created',
    sortType: (a, b) => sortByDate(a.values.created, b.values.created),
    Cell: ({ value, row }) => {
      if (!value) return <div>-</div>
      return <BlackParagraph text={formatDate(value, DATE_TIME_FORMAT, row.values.timezone || 'UTC')} size="12" />
    },
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.operator')} />
    },
    accessor: 'admin_username',
    sortType: (a, b) => sortByAlphabet(a.values.admin_username, b.values.admin_username),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.role')} />
    },
    accessor: 'adminRole',
    sortType: (a, b) => sortByAlphabet(a.values.adminRole, b.values.adminRole),
    Cell: ({ value, state: { globalFilter } }) => (
      <Label
        text={highlight(value, globalFilter.search) as string}
        color={value === Roles.SuperAdmin ? '#d9ae44c2' : '#3a8f5ad4'}
      />
    ),
    width: 120,
  },

  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.origin')} />
    },
    accessor: 'entity_name',
    sortType: (a, b) => sortByAlphabet(a.values.entity_name, b.values.entity_name),
    Cell: ({ value, state: { globalFilter } }) => {
      return <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    },
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.action')} />
    },
    accessor: 'message',
    Cell: ({ value, state: { globalFilter } }) => {
      if (!value) return <div>-</div>
      return (
        <BlackParagraph
          style={{ flexDirection: 'row-reverse', whiteSpace: 'pre-wrap' }}
          text={highlight(value, globalFilter.search)}
          size="12"
        />
      )
    },
    width: 200,
  },
  {
    Header: () => null,
    accessor: 'entity',
    disableSortBy: true,
    Cell: () => {
      return <div />
    },
    width: 0,
  },
]

const generateMessage = ({
  entity_name,
  type,
  admin_username,
  data,
}: {
  entity_name: string
  type: ActivityLog['type']
  admin_username: string
  data: any
}) => {
  const getMessageKey = (type: ActivityLog['type']) => {
    switch (type) {
      case 'ADD':
        return 'activityLog.entityAdded'
      case 'DELETE':
        return 'activityLog.entityDeleted'
      case 'UPDATE':
        return 'activityLog.entityUpdated'
    }
  }

  let title = ''
  title ||= data?.title || data?.firstname || data?.user_id || ''

  return i18n.t(getMessageKey(type), { entity: entity_name, title, admin: admin_username })
}

//const initialGlobalFilter = { search: '', actions: [] }

const Log: FC = () => {
  const [t] = useTranslation()
  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const role = state.admin?.type
  const [pagination, setPagination] = useState(() => ({ pageIndex: 0, pageSize: 50 }))
  const [sortBy, setSortBy] = useState<SortingRule<FormattedActivityLog>[]>(() => [{ id: 'created', desc: true }])
  const [search, setSearch] = useState('')
  const [actions, setActions] = useState<string[]>(() => [])

  const { data: logs, isLoading } = useQuery(
    ['activity-log', i_realm, pagination, sortBy[0], search, actions],
    getActivityLogFn,
    {
      select: (data) => {
        const items = data.items
          .filter(({ admin_user }) => (role === Roles.SuperAdmin ? true : admin_user?.type !== Roles.SuperAdmin))
          .map(({ admin_user, message, ...log }) => ({
            adminRole: admin_user?.type,
            message:
              message ||
              generateMessage({
                entity_name: log.entity_name,
                type: log.type,
                admin_username: log.admin_username,
                data: log.data,
              }),
            ...log,
          }))
        return { items, total: data.total }
      },
    }
  )

  const pageCount = useMemo(
    () => Math.ceil(((logs?.total as number) || 0) / pagination.pageSize) || 0,
    [logs?.total, pagination.pageSize]
  )

  const tableProps = useTable(
    {
      columns,
      data: logs?.items ?? emptyArray,
      initialState: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        globalFilter: { search },
        sortBy,
      }, // Pass our hoisted table state
      //globalFilter: globalFilterFunc,
      autoResetPage: false,
      disableSortRemove: true,
      manualPagination: true,
      manualSortBy: true,
      manualGlobalFilter: true,
      matablePropsnualGlobalFilter: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  )

  useEffect(() => {
    const { pageIndex, pageSize, sortBy: _sortBy } = tableProps.state
    if (pageIndex !== pagination.pageIndex || pageSize !== pagination.pageSize) {
      setPagination({ pageIndex, pageSize })
    }

    if (_sortBy[0]) {
      const item = _sortBy[0]
      const currentItem = sortBy[0]
      if (!currentItem || item.id !== currentItem?.id || item?.desc !== currentItem?.desc) {
        setSortBy([item])
      }
    }
  }, [tableProps.state, pagination, sortBy])

  const setSearchQuery = (query: string) => {
    tableProps.setGlobalFilter({
      ...tableProps.state.globalFilter,
      search: query,
    })
    setSearch(query)
  }

  //if (isLoading || !logs) {
  //  return <LoadingPage />
  //}

  return (
    <Page title={t('page.activity.title')}>
      <PageHeader
        title={t('page.activity.title')}
        subtitle={logs?.total || ''}
        search={search}
        filter={
          <LogsFilter
            actions={actions}
            onChangeFilter={(action) => {
              if (actions.includes(action)) {
                setActions(actions.filter((a) => a !== action))
              } else {
                setActions([...actions, action])
              }
            }}
          />
        }
        isFilterActive={!!actions.length}
        onClearFilters={() => {
          setSearchQuery('')
          setActions([])
        }}
        onChangeSearch={(query) => {
          setSearchQuery(query)
        }}
      />
      <Table
        isLoading={isLoading}
        {...tableProps}
        setGlobalFilter={() => {
          setSearchQuery('')
          setActions([])
        }}
      />
    </Page>
  )
}

export default Log
